/**
 * Живой кэф калькулятора: эта нога, не сосед и не чужое число на странице.
 *
 * Своя вилка с ленты — насос этой id. Соседнюю карточку не берём: у неё
 * другой рынок, harvest клеил полки (карты vs матч). Страница — только если
 * сверка узнала ту же ставку: ячейка Thunderpick, ask полика, и formFromClosest
 * не отверг 33¢ / чужой кэф. Снял галочку — застыл, как Auto у BreakingBet.
 */

import { useSyncExternalStore } from 'react';

import { Decimal } from 'decimal.js';

import { marketParts, marketPrefix, type Fork, type ForkLeg } from '../lib/protocol';
import { healShareFork, isShareLeg } from '../lib/shareVenue';
import { marketPeriod } from '../lib/sports';

/**
 * Пульс обновляет форму раз в секунду. Без нового чтения через 5 с
 * снова лента — иначе первый снимок залипает навсегда.
 */
export const PAGE_FRESH_MS = 5_000;

const NONE: Record<string, string> = {};

interface PageHit {
  form: string;
  at: number;
  /** С площадки через сверку, не сырой дамп 33,3. Скачок дальше PAGE_NEAR берём. */
  site?: boolean;
  /** Линия снята: кэф ещё стоит, поставить нельзя. */
  shut?: boolean;
}

let pages = new Map<string, Record<string, PageHit>>();
/** Первый ROI стола, когда обе ноги уже с касс. */
let bornRoi = new Map<string, string>();
let pageTick = 0;
const listeners = new Set<() => void>();

function emit(): void {
  pageTick += 1;
  for (const listen of listeners) listen();
}

function subscribe(listen: () => void): () => void {
  listeners.add(listen);
  return () => {
    listeners.delete(listen);
  };
}

/** Подписка: страница прислала новый кэф. */
export function usePageTick(): number {
  return useSyncExternalStore(subscribe, () => pageTick, () => 0);
}

export function setPageForm(
  forkId: string,
  source: string,
  form: string,
  fromSite = false,
  at = Date.now(),
  shut = false,
): void {
  if (forkId === '' || source === '' || form.trim() === '') return;
  const rec = { ...(pages.get(forkId) ?? {}) };
  rec[source] = { form, at, site: fromSite, ...(shut ? { shut: true } : {}) };
  pages.set(forkId, rec);
  emit();
}

export function dropPages(forkId: string): void {
  if (!pages.has(forkId) && !bornRoi.has(forkId)) return;
  pages.delete(forkId);
  bornRoi.delete(forkId);
  emit();
}

/**
 * Полка переехала на переизданную вилку — кэф кассы едет следом. Это чтение
 * одной ставки: переносим только на ногу той же ставки, чужой рынок того же
 * матча не красим. Старый id не трогаем: на нём может стоять карточка.
 */
export function carryPages(from: Fork, to: Fork): void {
  const rec = pages.get(from.id);
  if (from.id === to.id || rec === undefined || pages.has(to.id)) return;
  const kept: Record<string, PageHit> = {};
  for (const [source, hit] of Object.entries(rec)) {
    const was = from.legs?.find((leg) => leg.s === source);
    const now = to.legs?.find((leg) => leg.s === source);
    if (was !== undefined && now !== undefined && sameBet(from, was, to, now)) kept[source] = hit;
  }
  const carried = Object.keys(kept).length;
  if (carried === 0) return;
  pages.set(to.id, kept);
  const born = bornRoi.get(from.id);
  if (born !== undefined && carried === Object.keys(rec).length && !bornRoi.has(to.id)) {
    bornRoi.set(to.id, born);
  }
  emit();
}

/** Тесты: вычистить кэфы страниц. */
export function resetPages(): void {
  if (pages.size === 0 && bornRoi.size === 0) return;
  pages = new Map();
  bornRoi = new Map();
  emit();
}

/** Сколько мс назад сверка узнала эту ногу. Нет чтения — null. */
export function pageAgeMs(forkId: string, source: string, now = Date.now()): number | null {
  const hit = pages.get(forkId)?.[source];
  if (hit === undefined || hit.site !== true) return null;
  return now - hit.at;
}

/** Сверка видела свою ячейку и линия снята. Держим, пока не придёт новое чтение. */
export function pageShut(forkId: string, source: string, now = Date.now()): boolean {
  const hit = pages.get(forkId)?.[source];
  if (hit === undefined || hit.site !== true || hit.shut !== true) return false;
  if (now - hit.at > PAGE_FRESH_MS) return false;
  return true;
}

export function bornRoiOf(forkId: string): string | null {
  return bornRoi.get(forkId) ?? null;
}

export function rememberBornRoi(forkId: string, roiPct: string): void {
  if (forkId === '' || bornRoi.has(forkId)) return;
  try {
    const n = new Decimal(roiPct);
    if (!n.isFinite()) return;
    bornRoi.set(forkId, n.toFixed(4));
  } catch {
    return;
  }
}

export function pageBy(forkId: string, now = Date.now()): Record<string, string> {
  const rec = pages.get(forkId);
  if (rec === undefined) return NONE;
  const out: Record<string, string> = {};
  for (const [source, hit] of Object.entries(rec)) {
    if (now - hit.at <= PAGE_FRESH_MS) out[source] = hit.form;
  }
  return Object.keys(out).length === 0 ? NONE : out;
}

/** Ноги, чей кэф сверка узнала как эту ставку, не дамп innerText. */
export function pageSite(forkId: string, now = Date.now()): ReadonlySet<string> {
  const rec = pages.get(forkId);
  if (rec === undefined) return new Set();
  const out = new Set<string>();
  for (const [source, hit] of Object.entries(rec)) {
    if (hit.site === true && now - hit.at <= PAGE_FRESH_MS) out.add(source);
  }
  return out;
}

/** Биржа печатает 2.17, лента — 2.388. Сверка режет по числу знаков и 2.17 не видит. */
export function snapOddsOf(leg: ForkLeg): string {
  const raw = String(leg.odds ?? '').trim().replace(',', '.');
  if (!isShare(leg)) return raw;
  try {
    const n = new Decimal(raw);
    if (!n.isFinite() || !n.gt(1)) return raw;
    return n.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2);
  } catch {
    return raw;
  }
}

function periodOf(fork: { market?: string }, leg: ForkLeg): string {
  return leg.period ?? marketPeriod(fork.market) ?? '';
}

function lineOf(fork: { market?: string }): number | null {
  return marketParts(fork.market).line;
}

/** Одна и та же ставка: тот же матч, та же контора, тот же исход. */
export function sameBet(
  want: { ev?: string; market?: string },
  wantLeg: ForkLeg,
  have: { ev?: string; market?: string },
  haveLeg: ForkLeg,
): boolean {
  if ((want.ev ?? '') === '' || want.ev !== have.ev) return false;
  if (wantLeg.s !== haveLeg.s) return false;
  if (wantLeg.sel && haveLeg.sel) return wantLeg.sel === haveLeg.sel;
  if (wantLeg.asset && haveLeg.asset) return wantLeg.asset === haveLeg.asset;
  if (wantLeg.side !== haveLeg.side) return false;
  if (periodOf(want, wantLeg) !== periodOf(have, haveLeg)) return false;
  if (marketPrefix(want.market) !== marketPrefix(have.market)) return false;
  if (lineOf(want) !== lineOf(have)) return false;
  return true;
}

/** Свежие ноги с соседних живых вилок того же матча. Свою карточку не берём. */
export function harvest(
  want: Fork,
  live: Iterable<Fork>,
  locked: ReadonlySet<string> = new Set(),
): Map<string, ForkLeg> {
  const found = new Map<string, { leg: ForkLeg; at: number }>();
  for (const have of live) {
    if (have.id === want.id) continue;
    for (const wantLeg of want.legs ?? []) {
      if (locked.has(wantLeg.s)) continue;
      for (const haveLeg of have.legs ?? []) {
        if (!sameBet(want, wantLeg, have, haveLeg)) continue;
        const at = have.at ?? 0;
        const prev = found.get(wantLeg.s);
        if (prev === undefined || at >= prev.at) {
          found.set(wantLeg.s, { leg: haveLeg, at });
        }
      }
    }
  }
  return new Map([...found].map(([source, hit]) => [source, hit.leg]));
}

export function applyFresh(fork: Fork, quotes: Map<string, ForkLeg>): Fork {
  if (quotes.size === 0 || fork.legs === undefined) return fork;
  let changed = false;
  const legs = fork.legs.map((leg) => {
    const next = quotes.get(leg.s);
    if (next === undefined) return leg;
    if (next.odds === leg.odds && next.share === leg.share) return leg;
    changed = true;
    return { ...leg, odds: next.odds, share: next.share, book: next.book ?? leg.book };
  });
  if (!changed) return fork;
  return { ...fork, legs, o: legs.map((leg) => leg.odds) };
}

function isShare(leg: ForkLeg): boolean {
  return isShareLeg(leg);
}

/**
 * Дальше этого страница — чужой рынок, не уехавший кэф.
 * 33¢ полика против живых 52¢ (Сергей, 19.09: в вилке верно, в столе 33,3).
 */
export const PAGE_NEAR = 0.15;

/** Выше — центы на ленте полика, не кэф. 33.3 как раз отсюда. */
const ODDS_CEILING = 25;

function impliedOf(odds: number): number | null {
  if (!Number.isFinite(odds) || !(odds > 1)) return null;
  return 1 / odds;
}

/** Кэф со страницы → то же письмо, что замок: кэф у конторы, центы у биржи. */
export function formFromClosest(
  leg: ForkLeg,
  closest: number,
  current: string = leg.odds,
): string | null {
  if (!Number.isFinite(closest) || !(closest > 1)) return null;
  const now = Number(String(current).trim().replace(',', '.'));
  const cur = impliedOf(now);
  if (isShare(leg)) {
    // На полике в тексте и кэф, и центы. 33.3 — цена шара, не кэф 33.
    const fromOdds = closest < ODDS_CEILING ? 1 / closest : null;
    const fromCents = closest < 100 ? closest / 100 : null;
    let price: number | null = null;
    if (fromOdds !== null && fromCents !== null && cur !== null) {
      price = Math.abs(fromOdds - cur) <= Math.abs(fromCents - cur) ? fromOdds : fromCents;
    } else {
      price = fromOdds ?? fromCents;
    }
    if (price === null || !(price > 0) || !(price < 1)) return null;
    // 33¢ и чужая полка (карты 2.32 vs матч 1.40) — не уехавший кэф этой ноги.
    if (cur !== null && Math.abs(price - cur) > PAGE_NEAR) return null;
    return (price * 100).toFixed(1);
  }
  if (cur !== null) {
    const page = impliedOf(closest);
    if (page === null || Math.abs(page - cur) > PAGE_NEAR) return null;
  }
  return new Decimal(closest).toDecimalPlaces(3, Decimal.ROUND_DOWN).toFixed(3);
}

function paintLeg(leg: ForkLeg, raw: string): ForkLeg | null {
  try {
    const n = new Decimal(raw.trim().replace(',', '.'));
    if (!n.isFinite() || !n.gt(0)) return null;
    if (isShare(leg)) {
      const price = n.div(100);
      if (price.lte(0) || price.gte(1)) return null;
      const odds = new Decimal(1).div(price).toDecimalPlaces(3, Decimal.ROUND_DOWN).toFixed(3);
      const share = price.toDecimalPlaces(3, Decimal.ROUND_UP).toString();
      return { ...leg, odds, share };
    }
    if (n.lte(1)) return null;
    return { ...leg, odds: n.toDecimalPlaces(3, Decimal.ROUND_DOWN).toFixed(3) };
  } catch {
    return null;
  }
}

/** Подставить кэф страницы. Застывшие источники не трогаем. */
export function applyForms(
  fork: Fork,
  by: Record<string, string>,
  locked: ReadonlySet<string> = new Set(),
): Fork {
  if (fork.legs === undefined || Object.keys(by).length === 0) return fork;
  let changed = false;
  const legs = fork.legs.map((leg) => {
    if (locked.has(leg.s)) return leg;
    const raw = by[leg.s];
    if (raw === undefined) return leg;
    const next = paintLeg(leg, raw);
    if (next === null || next.odds === leg.odds) return leg;
    changed = true;
    return next;
  });
  if (!changed) return fork;
  return { ...fork, legs, o: legs.map((leg) => leg.odds) };
}

/**
 * Запомнить кэф сверки, только если это та же ставка.
 * `trusted === false` — ближайшее число в тексте, не ячейка и не ask.
 * Старый архив поле не шлёт: решаем formFromClosest (33¢ и чужой кэф режет).
 */
export function rememberSite(
  forkId: string,
  fork: Fork,
  rows: readonly {
    venue: string;
    closest: number | null;
    state: string;
    trusted?: boolean;
    suspended?: boolean;
  }[],
  now = Date.now(),
): void {
  if (forkId === '' || fork.legs === undefined) return;
  for (const row of rows) {
    if (row.trusted === false) continue;
    const shut = row.suspended === true;
    if (row.closest === null) {
      if (!shut) continue;
      const prev = pages.get(forkId)?.[row.venue]?.form;
      if (prev === undefined) continue;
      setPageForm(forkId, row.venue, prev, true, now, true);
      continue;
    }
    if (row.state !== 'moved' && row.state !== 'match' && !shut) continue;
    const leg = fork.legs.find((one) => one.s === row.venue);
    if (leg === undefined) continue;
    const form = formFromClosest(leg, row.closest, leg.odds);
    if (form === null) {
      if (!shut) continue;
      const prev = pages.get(forkId)?.[row.venue]?.form;
      if (prev === undefined) continue;
      setPageForm(forkId, row.venue, prev, true, now, true);
      continue;
    }
    setPageForm(forkId, row.venue, form, true, now, shut);
  }
}

/** Своя нога ленты. Страница — только с ключом сверки. Соседа не берём. */
export function paintLive(
  fork: Fork,
  live: Iterable<Fork>,
  page: Record<string, string>,
  locked: ReadonlySet<string>,
  _selfLive?: boolean,
  fromSite: ReadonlySet<string> = new Set(),
): Fork {
  void live;
  void _selfLive;
  const forms: Record<string, string> = {};
  for (const [source, form] of Object.entries(page)) {
    if (locked.has(source) || !fromSite.has(source)) continue;
    forms[source] = form;
  }
  return healShareFork(applyForms(fork, forms, locked));
}
